// 9fbef606107a605d69c0edbcd8029e5d
/**
 * usePaintReady Hook
 *
 * Intention:
 * - Traditionally, skeletons were handled at the root level with a single giant shimmer placeholder
 *   covering the entire page. This often caused layout rigidity, unnecessary re-renders,
 *   and flickers when different UI sections finished loading at different times.
 * - With this hook, skeleton handling is isolated to individual components.
 *   Each child decides when to show its own skeleton or real content
 *   based purely on paint readiness.
 *
 * What it does:
 * - Runs in both SSR and CSR safely.
 * - During SSR, `ready` is `false` by default so skeletons are rendered.
 * - After hydration on the client, it waits for the next paint cycle
 *   (via `requestAnimationFrame`) before marking `ready = true`.
 *
 * Benefits:
 * - Eliminates the need for one global skeleton at the root level.
 * - Each child can define its own skeleton UI (header, image, carousel, etc.).
 * - Smooth transition: prevents flickers by only swapping skeleton → real UI
 *   once the browser is ready to paint.
 * - Modular and scalable: every component independently manages its skeleton lifecycle.
 *
 */

import { useState, useEffect } from 'react';

export default function usePaintReady() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return ready;
}
